import * as React from "react";
import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ImplementationType,
  getImplementationTypeName,
} from "../../models/OrganizationConfiguration/ImplementationType";
import { SourceLink, SourceLinkOrigin } from "../../models/SourceLink";

/**
 * Names the row in the firm's own database, so a broken link can be chased back to the record that caused it.
 *
 * The tab leads because it is the part somebody can act on — an SE opens the Medical tab, never the MedRec table
 * it reads — and the two routinely differ. The physical table sits underneath as what makes the row distinct, and
 * the column is what makes the value lookupable: Correspondence is keyed by CorrespondID, Medical by DrVisitID.
 */
export const SourceReferenceCell: React.FC<{
  link: SourceLink;
  implementationType?: ImplementationType | null;
}> = ({ link, implementationType }) => {
  const [copied, setCopied] = useState(false);

  if (!link.sourceTable && !link.sourceRecordId) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-muted-foreground cursor-default">&mdash;</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          {/* Two causes, and the reader acts differently on each: a folder has no record to find, while a
              database source such as Needles has one the queries simply do not select. */}
          {link.origin === SourceLinkOrigin.ConfiguredFolder
            ? "This link came from a folder rather than a database record, so the path is the only reference back."
            : "This source does not record which database row the link came from, so the path is the only reference back."}
        </TooltipContent>
      </Tooltip>
    );
  }

  const predicate =
    link.sourceRecordColumn && link.sourceRecordId
      ? `${link.sourceRecordColumn} = ${link.sourceRecordId}`
      : link.sourceRecordId;

  // Carries both names when they differ, because whoever receives it may be working from either.
  const tableReference =
    link.sourceTabLabel && link.sourceTabLabel !== link.sourceTable
      ? `${link.sourceTabLabel} (${link.sourceTable})`
      : (link.sourceTabLabel ?? link.sourceTable);

  const reference = [getImplementationTypeName(implementationType), tableReference, predicate]
    .filter(Boolean)
    .join(" \u00b7 ");

  return (
    <div className="flex items-center gap-1 min-w-0">
      <div className="flex flex-col min-w-0">
        <span className="truncate text-sm">{link.sourceTabLabel ?? link.sourceTable}</span>
        <span className="truncate text-xs text-muted-foreground font-mono">{predicate}</span>
        {link.sourceTabLabel && link.sourceTabLabel !== link.sourceTable && (
          <span className="truncate text-xs text-muted-foreground">{link.sourceTable}</span>
        )}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            aria-label="Copy source reference"
            onClick={async () => {
              // Clipboard access can be refused outright, and swallowing that would leave a button that looks
              // like it worked.
              try {
                await navigator.clipboard.writeText(reference);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {
                setCopied(false);
              }
            }}
          >
            {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent className="font-mono">{reference}</TooltipContent>
      </Tooltip>
    </div>
  );
};

export default SourceReferenceCell;
